import { i18n } from '@lingui/core'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { AgenticMarkdown } from 'designSystem/AgenticMarkdown'
import { useOptionalSidePanelPresentation } from 'designSystem/components/sidePanelContext'
import { Text } from 'designSystem/components/Text'
import { ArtifactCard } from 'features/knowledgeDocumentViewer/artifacts/ArtifactCard'
import { useArtifactActions } from 'features/knowledgeDocumentViewer/artifacts/useArtifactActions'
import { CitedResponseSegments } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/CitedResponseSegments'
import {
  getDocChatTurnStatus,
  shouldShowDocChatTurnThoughts,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import {
  DOC_CHAT_FALLBACK_NOTICE,
  DOC_CHAT_STOPPED_NOTICE,
  getDocChatErrorNotice,
  getDocChatResponseBodyText,
  getDocChatResponseStatusText,
  getDocChatSuggestedQuestions,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatText'
import { inspectionComponentId } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import type { PreviewTrendHandler } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import type {
  DocChatArtifactEvent,
  DocChatResponseEvent,
  DocChatResponsePartialEvent,
  DocChatThoughtEvent,
  DocChatTurn,
} from 'features/knowledgeDocumentViewer/types'
import {
  LotoProposalCard,
  type OpenLotoPage,
} from 'features/lotoReports/components/LotoProposalCard'
import type { LotoReport } from 'features/lotoReports/lotoReportSchemas'
import { useLotoScope } from 'features/lotoReports/useLotoPage'
import React, { useCallback } from 'react'

import { AgenticInlineDocumentResults } from './AgenticInlineDocumentResults'
import { DocChatCollapsibleThoughts } from './DocChatCollapsibleThoughts'
import { DocChatInspectionCard } from './DocChatInspectionCard'
import { DocChatMessageActions } from './DocChatMessageActions'
import { DocChatRevisionResults } from './DocChatRevisionResults'
import {
  ChatBody,
  ChatResponseText,
  ChatTurnRoot,
  FallbackNotice,
  UserMessageBubble,
} from './docChatStyles'
import { DocChatTurnSuggestions } from './DocChatSuggestedQuestions'
import { DocChatTagPickerCard } from './DocChatTagPickerCard'
import { DocChatTrendCard } from './DocChatTrendCard'
import { DocChatTurnAttachments } from './DocChatTurnAttachments'
import type {
  NavigateDocumentHandler,
  PreviewDocumentHandler,
  PreviewImageHandler,
  PreviewInspectionHandler,
  PreviewRevisionHandler,
} from './previewDocumentHandler'

function getTurnCopyText({
  errorMessage,
  hasError,
  partialEvent,
  responseEvent,
}: {
  errorMessage?: string
  hasError: boolean
  partialEvent?: DocChatResponsePartialEvent
  responseEvent?: DocChatResponseEvent
}): string {
  if (responseEvent) {
    const bodyText = getDocChatResponseBodyText(responseEvent)
    if (bodyText) return bodyText
    return getDocChatResponseStatusText(responseEvent)
  }
  if (partialEvent) {
    return getDocChatResponseBodyText(partialEvent)
  }
  if (hasError) {
    return getDocChatErrorNotice(errorMessage)
  }
  return ''
}

function DocChatFallbackNotice(): React.ReactElement {
  useLingui()
  return (
    <FallbackNotice role='status'>
      <Text as='p' variant='label' color='default'>
        {i18n._(DOC_CHAT_FALLBACK_NOTICE)}
      </Text>
      <Text as='p' variant='caption' color='muted'>
        <Trans>
          There is likely an issue with the server. Please try again. If the
          problem persists, please contact support.
        </Trans>
      </Text>
    </FallbackNotice>
  )
}

function DocChatLotoProposal({
  proposal,
  onPreviewDocument,
}: {
  proposal: LotoReport
  onPreviewDocument: PreviewDocumentHandler
}): React.ReactElement | null {
  const scope = useLotoScope()
  const sidePanel = useOptionalSidePanelPresentation()
  const openPage = useCallback<OpenLotoPage>(
    (document, pageNumber) => {
      sidePanel?.keepOpenOnNextOutsideClick()
      onPreviewDocument(document, pageNumber)
    },
    [onPreviewDocument, sidePanel]
  )
  if (!scope) return null
  return (
    <LotoProposalCard scope={scope} proposal={proposal} onOpenPage={openPage} />
  )
}

export function DocChatTurnRows({
  onPreviewDocument,
  onPreviewTrend,
  onPreviewImage,
  onPreviewInspection,
  onNavigateDocument,
  onPreviewRevision,

  conversationId = null,
  showActions = false,
  showSuggestions = false,
  turn,
}: {
  onPreviewDocument: PreviewDocumentHandler
  onPreviewTrend?: PreviewTrendHandler
  onPreviewImage?: PreviewImageHandler
  onPreviewInspection?: PreviewInspectionHandler
  onNavigateDocument?: NavigateDocumentHandler
  onPreviewRevision?: PreviewRevisionHandler
  conversationId?: string | null
  showActions?: boolean
  showSuggestions?: boolean
  turn: DocChatTurn
}): React.ReactElement {
  useLingui()
  const { canUseArtifacts, openInPanel, openInTab } = useArtifactActions()
  const submittedAttachments = turn.events.find(
    (event) => event.type === 'submitted_query'
  )?.attachments
  const status = getDocChatTurnStatus(turn)
  const isLoading = status === 'loading'
  const isCancelled = status === 'cancelled'

  const thoughts = turn.events.filter(
    (event): event is DocChatThoughtEvent => event.type === 'thought'
  )
  const responseEvent =
    turn.events.find(
      (event): event is DocChatResponseEvent => event.type === 'response'
    ) ?? turn.pendingResponse
  const partialEvent = turn.events.find(
    (event): event is DocChatResponsePartialEvent =>
      event.type === 'response_partial'
  )
  const artifactEvents = turn.events.filter(
    (event): event is DocChatArtifactEvent => event.type === 'artifact'
  )
  const bodyEvent = responseEvent ?? partialEvent

  const errorEvent = turn.events.find((event) => event.type === 'error')
  const hasError = errorEvent !== undefined
  const showThoughts = !isCancelled && shouldShowDocChatTurnThoughts(turn)
  const copyText = getTurnCopyText({
    errorMessage: errorEvent?.message,
    hasError,
    partialEvent,
    responseEvent,
  })
  const isFallback = responseEvent?.metadata.is_fallback === true
  const suggestedQuestions =
    showSuggestions && responseEvent && !isFallback
      ? getDocChatSuggestedQuestions(responseEvent)
      : []

  return (
    <ChatTurnRoot>
      <DocChatTurnAttachments
        attachments={submittedAttachments}
        onPreviewImage={onPreviewImage}
        align='end'
      />
      {turn.query ? (
        <UserMessageBubble>
          <AgenticMarkdown source='user'>{turn.query}</AgenticMarkdown>
        </UserMessageBubble>
      ) : null}
      <ChatBody>
        {showThoughts ? (
          <DocChatCollapsibleThoughts
            isThinking={isLoading && !turn.pendingResponse}
            isSavingResponse={isLoading && turn.pendingResponse !== undefined}
            responseEvent={responseEvent}
            thoughts={thoughts}
          />
        ) : null}
        {artifactEvents.map((event) => (
          <ArtifactCard
            key={event.id}
            event={event}
            onOpen={openInPanel}
            onOpenInTab={openInTab}
            actionsDisabled={!canUseArtifacts}
          />
        ))}
        {isFallback ? <DocChatFallbackNotice /> : null}
        {bodyEvent && !isFallback ? (
          <>
            <CitedResponseSegments
              event={bodyEvent}
              onPreviewDocument={onPreviewDocument}
              onPreviewTrend={onPreviewTrend}
              ResponseText={ChatResponseText}
            />
            {responseEvent?.time_series?.trend && onPreviewTrend ? (
              <DocChatTrendCard
                trend={responseEvent.time_series.trend}
                onPreviewTrend={onPreviewTrend}
              />
            ) : null}
            {responseEvent?.inspection_results?.map((result, index) => (
              <DocChatInspectionCard
                key={`${result.kind === 'report' ? result.site_id : result.kind === 'nii_assessment' ? result.ticket_id : inspectionComponentId(result)}-${index}`}
                result={result}
                onPreview={onPreviewInspection}
              />
            ))}
            {responseEvent?.revision_results?.length ? (
              <DocChatRevisionResults
                results={responseEvent.revision_results}
                onNavigateDocument={onNavigateDocument}
                onPreviewRevision={onPreviewRevision}
              />
            ) : null}
            {responseEvent?.loto_report ? (
              <DocChatLotoProposal
                proposal={responseEvent.loto_report}
                onPreviewDocument={onPreviewDocument}
              />
            ) : null}
            {responseEvent?.time_series ? (
              <DocChatTagPickerCard
                resolutions={responseEvent.time_series.resolutions}
                turnId={turn.id}
                onPreviewTrend={onPreviewTrend}
              />
            ) : null}
            <AgenticInlineDocumentResults
              event={bodyEvent}
              onPreviewDocument={onPreviewDocument}
            />
          </>
        ) : null}
        {hasError ? (
          <Text as='p' variant='body' color='muted'>
            {getDocChatErrorNotice(errorEvent?.message)}
          </Text>
        ) : null}
        {isCancelled ? (
          <Text as='p' variant='body' color='muted'>
            {i18n._(DOC_CHAT_STOPPED_NOTICE)}
          </Text>
        ) : null}
        {showActions && !isLoading ? (
          <DocChatMessageActions
            canSaveEvalCase={Boolean(
              responseEvent &&
              !isFallback &&
              !hasError &&
              !isCancelled &&
              copyText.trim() &&
              turn.query.trim()
            )}
            conversationId={conversationId}
            copyText={copyText}
            response={responseEvent}
            retryQuery={turn.query}
            onPreviewDocument={onPreviewDocument}
          />
        ) : null}
      </ChatBody>
      {suggestedQuestions.length > 0 ? (
        <DocChatTurnSuggestions questions={suggestedQuestions} />
      ) : null}
    </ChatTurnRoot>
  )
}
